import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { insidePages } from '../data/insidePages';
import { OFFER_ANCHOR } from './constants';
export function LookInside() {
  const trackRef = useRef<HTMLUListElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const scrollToIndex = useCallback((index: number) => {
    const track = trackRef.current;
    if (!track) return;
    const clamped = Math.max(0, Math.min(index, insidePages.length - 1));
    const slide = track.children[clamped] as HTMLElement | undefined;
    if (slide) {
      track.scrollTo({
        left: slide.offsetLeft - track.offsetLeft,
        behavior: 'smooth'
      });
    }
  }, []);
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    const handleScroll = () => {
      const slides = Array.from(track.children) as HTMLElement[];
      const center = track.scrollLeft + track.clientWidth / 2;
      let closest = 0;
      let smallest = Number.POSITIVE_INFINITY;
      slides.forEach((slide, index) => {
        const slideCenter = slide.offsetLeft - track.offsetLeft + slide.clientWidth / 2;
        const distance = Math.abs(slideCenter - center);
        if (distance < smallest) {
          smallest = distance;
          closest = index;
        }
      });
      setActiveIndex(closest);
    };
    track.addEventListener('scroll', handleScroll, {
      passive: true
    });
    return () => track.removeEventListener('scroll', handleScroll);
  }, []);
  return <section className="border-y border-brand-line bg-brand-paper px-4 py-5" data-id="element-92" data-mp="3igmy3lnffar7">
      <div className="mx-auto max-w-5xl" data-id="element-93" data-mp="13as9q8f4ysg">
        <div className="mb-3 text-center" data-id="element-94" data-mp="3sgxcvap40q8h">
          <p className="mb-0.5 font-cond text-[11px] font-bold uppercase tracking-[0.2em] text-brand-carbon sm:text-sm" data-id="element-95" data-mp="3sf2rjv9xew3v">
            Look Inside
          </p>
          <h2 className="mb-1 font-display text-[20px] leading-tight sm:text-3xl" data-id="element-96" data-mp="2zsi0yx19yo1d">
            Discover What&apos;s Inside DIESEL DIAGNOSTIC PRO™
          </h2>
          <p className="mx-auto max-w-3xl text-[13px] font-medium leading-snug text-brand-muted sm:text-base" data-id="element-97" data-mp="13mr4ppins6ct">
            Visual diagnostic workflows, testing sequences, fuel-system references, scan-data
            guides, turbo and boost diagnostics, aftertreatment fault paths, symptom-based decision
            trees and ready-to-use workshop tools.
          </p>
        </div>

        <div className="relative" data-id="element-98" data-mp="krk9qnyu8dy6">
          <ul ref={trackRef} className="no-scrollbar flex snap-x snap-mandatory gap-3 overflow-x-auto scroll-smooth pb-2" data-id="element-99" data-mp="pp2jggkoix78">
            {insidePages.map(page => <li key={page.id} className="w-[88%] shrink-0 snap-center sm:w-[64%] lg:w-[50%]" data-id="element-100" data-mp="tpw7d9y4gc04">
                <a href={OFFER_ANCHOR} className="block overflow-hidden rounded-xl border-2 border-brand-carbon bg-white shadow-xl" aria-label={`${page.caption} — go to the offer`} data-id="element-101" data-mp="yqjwfq2siirp">
                  <img src={page.src} alt={page.alt} loading="lazy" className="block w-full object-contain" data-id="element-102" data-mp="2a44go2tvsott" />
                  <p className="border-t-2 border-brand-yellow bg-brand-carbon px-3 py-2 text-center font-cond text-[11px] font-bold uppercase tracking-wide text-white sm:text-xs" data-id="element-103" data-mp="2k7t23opi2e8x">
                    {page.caption}
                  </p>
                </a>
              </li>)}
          </ul>

          <button type="button" onClick={() => scrollToIndex(activeIndex - 1)} disabled={activeIndex === 0} className="absolute left-0 top-1/2 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border-2 border-brand-carbon bg-brand-yellow text-brand-carbon shadow-md transition-colors duration-150 ease-out hover:bg-brand-gold disabled:opacity-40 sm:flex" aria-label="Previous page" data-id="element-104" data-mp="26i8ykgo3hcyk">
            <ChevronLeftIcon className="h-5 w-5" data-id="element-105" data-mp="1njmxh1skvlun" />
          </button>
          <button type="button" onClick={() => scrollToIndex(activeIndex + 1)} disabled={activeIndex === insidePages.length - 1} className="absolute right-0 top-1/2 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border-2 border-brand-carbon bg-brand-yellow text-brand-carbon shadow-md transition-colors duration-150 ease-out hover:bg-brand-gold disabled:opacity-40 sm:flex" aria-label="Next page" data-id="element-106" data-mp="3lxy67af1jjev">
            <ChevronRightIcon className="h-5 w-5" data-id="element-107" data-mp="3fy34gey5dzd1" />
          </button>
        </div>

        <div className="mt-1 flex items-center justify-center gap-2" data-id="element-108" data-mp="3jzyeq8y23n2y">
          {insidePages.map((page, index) => <button key={page.id} type="button" onClick={() => scrollToIndex(index)} className={`h-2 rounded-full transition-[width,background-color] duration-200 ease-out ${index === activeIndex ? 'w-6 bg-brand-yellow' : 'w-2 bg-brand-line'}`} aria-label={`Go to page ${index + 1}: ${page.caption}`} aria-current={index === activeIndex} data-id="element-109" data-mp="3691qeub5xs1p" data-mpi={index} />)}
        </div>
        <p className="mt-1 text-center text-[11px] font-semibold text-brand-muted" data-id="element-110" data-mp="317vs8gyqhcks">
          Swipe to browse the 5 real pages from inside the system.
        </p>
      </div>
    </section>;
}